

import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import api from '../../api/axios'; // Use centralized api instance
import { FaExclamationCircle } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import { calculatePrices } from '../../utils/priceCalculator'; // 가격 계산기 import

const OPTION_TYPES = ['유료옵션', '할증', '무료서비스'];

const EditServiceRecordPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const [record, setRecord] = useState(null);
    const [menus, setMenus] = useState([]);
    const [options, setOptions] = useState([]);
    const [prices, setPrices] = useState({ servicePrices: [], optionPrices: [] });
    
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [selectedOptionIds, setSelectedOptionIds] = useState([]);
    
    const [calculatedPrice, setCalculatedPrice] = useState(0);
    const [adjustmentAmount, setAdjustmentAmount] = useState('');
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const [totalPrice, setTotalPrice] = useState(0);

    const [comment, setComment] = useState('');
    const [originalComment, setOriginalComment] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('카드');
    const [originalPaymentMethod, setOriginalPaymentMethod] = useState('카드');
    
    const [itemsChanged, setItemsChanged] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const canEditRecords = hasPermission(user?.role, 'records:edit');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [recordRes, menusRes, optionsRes, pricesRes] = await Promise.all([
                    api.get(`/jobs/${id}`),
                    api.get('/servicemenus'),
                    api.get('/options'),
                    api.get('/prices')
                ]);

                const recordData = recordRes.data.data;
                setRecord(recordData);
                setSelectedCourseId(recordData.courseId);
                setSelectedOptionIds(recordData.optionIds || []);
                
                const currentComment = recordData.priceBreakdown?.comment || recordData.comment || '';
                setComment(currentComment);
                setOriginalComment(currentComment);

                const currentPaymentMethod = recordData.paymentMethod || '카드';
                setPaymentMethod(currentPaymentMethod);
                setOriginalPaymentMethod(currentPaymentMethod);

                const adjustment = recordData.priceBreakdown?.adjustment;
                setAdjustmentAmount(adjustment?.amount || '');
                setAdjustmentReason(adjustment?.reason || '');

                setMenus(menusRes.data.data.filter(m => m.isActive));
                setOptions(optionsRes.data.data.filter(o => o.isActive));
                setPrices(pricesRes.data.data);

            } catch (err) {
                console.error("Error fetching data:", err);
                setError('데이터를 불러오는 데 실패했습니다.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    useEffect(() => {
        if (!record) return;

        const selectedMenu = menus.find(m => m.id === selectedCourseId);
        const currentSelectedOptions = options.filter(o => selectedOptionIds.includes(o.id));

        const { calculatedPrice, totalPrice } = calculatePrices({
            selectedMenu,
            selectedOptions: currentSelectedOptions,
            prices,
            sizeId: record.vehicle_size_id,
            adjustmentAmount
        });

        setCalculatedPrice(calculatedPrice);
        setTotalPrice(totalPrice);
    }, [selectedCourseId, selectedOptionIds, adjustmentAmount, record, menus, options, prices]);

    const groupedOptions = useMemo(() => {
        const groups = {};
        OPTION_TYPES.forEach(type => {
            groups[type] = options.filter(o => o.type === type).sort((a, b) => a.optionName.localeCompare(b.optionName));
        });
        return groups;
    }, [options]);

    const handleMenuChange = (courseId) => {
        setSelectedCourseId(courseId);
        setItemsChanged(true);
    };

    const handleOptionChange = (optionId) => {
        setSelectedOptionIds(prev => 
            prev.includes(optionId)
                ? prev.filter(id => id !== optionId)
                : [...prev, optionId]
        );
        setItemsChanged(true);
    };

    const handleAdjustmentChange = (e) => {
        const { name, value } = e.target;
        if (name === 'adjustmentAmount') setAdjustmentAmount(value);
        if (name === 'adjustmentReason') setAdjustmentReason(value);
        setItemsChanged(true);
    }

    const handleSave = async (e) => {
        e.preventDefault();
        
        const payload = {};
        let isMajorChange = false;

        if (itemsChanged) {
            if (!selectedCourseId) {
                setError('기본 메뉴를 선택해야 합니다.');
                return;
            }
            if (window.confirm('⚠ 주의: 과거 거래 금액을 현재 가격표 기준으로 덮어씁니다.\n\n원래 기록(스냅샷)이 사라지고 회계 기록이 변경됩니다. 정말 진행하시겠습니까?')) {
                isMajorChange = true;
                payload.courseId = selectedCourseId;
                payload.optionIds = selectedOptionIds;
                payload.finalPrice = totalPrice;
                payload.adjustmentAmount = parseFloat(adjustmentAmount) || 0;
                payload.adjustmentReason = adjustmentReason;
            } else {
                return; // Do not save if user cancels confirmation
            }
        }

        if (comment !== originalComment) {
            payload.comment = comment;
        }
        if (paymentMethod !== originalPaymentMethod) {
            payload.paymentMethod = paymentMethod;
        }
        
        // If it's a major change, we must include minor changes as well
        if (isMajorChange) {
            payload.comment = comment;
            payload.paymentMethod = paymentMethod;
        }

        if (Object.keys(payload).length === 0) {
            navigate('/admin/records');
            return;
        }

        setIsSaving(true);
        setError('');
        try {
            await api.put(`/jobs/${id}`, payload);
            navigate('/admin/records');
        } catch (err) {
            console.error("Error updating service record:", err);
            const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || '기록 저장에 실패했습니다.';
            setError(msg);
        } finally {
            setIsSaving(false);
        }
    };
    
    const getDisplayPrice = (item, type) => {
        if (!record || !record.vehicle_size_id) return null;
        const priceTable = type === 'menu' ? prices.servicePrices : prices.optionPrices;
        if (!priceTable) return null;
        const priceEntry = priceTable.find(p => 
            p[type === 'menu' ? 'menu_id' : 'option_id'] === item.id && 
            p.size_id === record.vehicle_size_id
        );
        return priceEntry ? parseFloat(priceEntry.price) : null;
    };

    if (loading) return <Container className="mt-4"><Spinner animation="border" /></Container>;
    if (error) return <Container className="mt-4"><Alert variant="danger">{error}</Alert></Container>;
    if (!record) return <Container className="mt-4"><p>기록을 찾을 수 없습니다.</p></Container>;

    const snapshotTotal = record.finalPrice != null ? parseInt(record.finalPrice) : null;
    const priceChanged = snapshotTotal != null && totalPrice !== snapshotTotal;
    const isPointPayment = record.status === 'completed' && record.paymentMethod === '포인트';

    return (
        <Container className="mt-4">
            <h3>서비스 기록 수정 (ID: {id})</h3>

            <Card className="mb-3">
                <Card.Body>
                    <div className="text-muted small mb-2" style={{ letterSpacing: '0.05em' }}>📌 당시 기록 (스냅샷)</div>
                    <Row className="align-items-center">
                        <Col md={8}>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                    padding: '0.5rem 0.85rem', borderRadius: 999,
                                    background: 'var(--bs-primary, #0d6efd)', color: '#fff',
                                    fontWeight: 600, fontSize: '0.95rem'
                                }}>
                                    🧾 {record.menuName || '(메뉴 없음)'}
                                </span>
                                {(record.optionsText || '').split(',').map(s => s.trim()).filter(Boolean).map((opt, i) => (
                                    <span key={i} style={{
                                        display: 'inline-flex', alignItems: 'center',
                                        padding: '0.4rem 0.75rem', borderRadius: 999,
                                        background: 'var(--bs-light, #f1f3f5)', color: 'var(--bs-body-color)',
                                        border: '1px solid var(--bs-border-color, #dee2e6)',
                                        fontSize: '0.85rem', fontWeight: 500
                                    }}>
                                        {opt}
                                    </span>
                                ))}
                            </div>
                            {record.paymentMethod && (
                                <div className="text-muted small">결제: {record.paymentMethod}</div>
                            )}
                        </Col>
                        <Col md={4} className="text-md-end">
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>당시 결제 금액</div>
                            <div className="text-primary fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>
                                {snapshotTotal != null ? snapshotTotal.toLocaleString() + '원' : '-'}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {isPointPayment && (
                <Alert variant="info" className="d-flex align-items-center">
                    <FaExclamationCircle className="me-2" />
                    <span>포인트 결제건은 저장 시 자동으로 환불·재차감됩니다. 잔액이 부족하면 저장이 거절됩니다.</span>
                </Alert>
            )}

            <Card>
                <Card.Header>차량 ID: {record.vehicleId} (수정 불가)</Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSave}>
                        <div className="mb-4">
                            <div className="text-muted small mb-2 fw-bold">기본 메뉴</div>
                            <div className="d-flex flex-wrap gap-2">
                                {menus.map(menu => {
                                    const price = getDisplayPrice(menu, 'menu');
                                    const isSelected = selectedCourseId === menu.id;
                                    const isDisabled = price === null || !canEditRecords;
                                    return (
                                        <button
                                            type="button"
                                            key={menu.id}
                                            onClick={() => !isDisabled && handleMenuChange(menu.id)}
                                            disabled={isDisabled}
                                            className="pos-tile"
                                            style={{
                                                minWidth: 140,
                                                padding: '0.9rem 1rem',
                                                borderRadius: 10,
                                                border: `2px solid ${isSelected ? 'var(--bs-primary, #0d6efd)' : 'var(--bs-border-color, #dee2e6)'}`,
                                                background: isSelected ? 'var(--bs-primary, #0d6efd)' : 'var(--bs-body-bg, #fff)',
                                                color: isSelected ? '#fff' : 'var(--bs-body-color, #212529)',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                opacity: isDisabled ? 0.5 : 1,
                                                transition: 'all 0.15s',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{menu.menuName}</div>
                                            <div style={{ fontSize: '0.85rem', opacity: isSelected ? 0.9 : 0.65, marginTop: 2 }}>
                                                {price !== null ? `${price.toLocaleString()}원` : '가격 미설정'}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mb-3">
                            <div className="text-muted small mb-2 fw-bold">추가 옵션</div>
                            {Object.keys(groupedOptions).map(type => (
                                groupedOptions[type].length > 0 && (
                                    <div key={type} className="mb-3">
                                        <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 6 }}>{type}</div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {groupedOptions[type].map(option => {
                                                const price = getDisplayPrice(option, 'option');
                                                const isSelected = selectedOptionIds.includes(option.id);
                                                const isDisabled = price === null || !canEditRecords;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={option.id}
                                                        onClick={() => !isDisabled && handleOptionChange(option.id)}
                                                        disabled={isDisabled}
                                                        className="pos-tile"
                                                        style={{
                                                            minWidth: 120,
                                                            padding: '0.7rem 0.9rem',
                                                            borderRadius: 10,
                                                            border: `2px solid ${isSelected ? 'var(--bs-success, #198754)' : 'var(--bs-border-color, #dee2e6)'}`,
                                                            background: isSelected ? 'var(--bs-success, #198754)' : 'var(--bs-body-bg, #fff)',
                                                            color: isSelected ? '#fff' : 'var(--bs-body-color, #212529)',
                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                            opacity: isDisabled ? 0.5 : 1,
                                                            transition: 'all 0.15s',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                            {isSelected && '✓ '}{option.optionName}
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', opacity: isSelected ? 0.9 : 0.65, marginTop: 2 }}>
                                                            {price !== null ? `+${price.toLocaleString()}원` : '가격 미설정'}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                        
                        <hr />

                        <div className="text-end">
                            <p className="mb-1 text-muted small">자동 계산 금액(현재 가격표 기준): {calculatedPrice.toLocaleString()}원</p>
                            <Row className="justify-content-end align-items-center mb-2">
                                <Col xs="auto"><Form.Label className="col-form-label">가격 조정</Form.Label></Col>
                                <Col xs={3}>
                                    <Form.Control type="number" name="adjustmentAmount" placeholder="조정액" value={adjustmentAmount} onChange={handleAdjustmentChange} disabled={!canEditRecords} />
                                </Col>
                                <Col xs={4}>
                                    <Form.Control type="text" name="adjustmentReason" placeholder="조정 사유" value={adjustmentReason} onChange={handleAdjustmentChange} disabled={!canEditRecords} />
                                </Col>
                            </Row>
                            <h4 className="mt-2 text-muted">재계산 예상 금액: {totalPrice.toLocaleString()}원</h4>
                        </div>

                        {!itemsChanged && priceChanged &&
                            <Alert variant="info" className="small">
                                ℹ️ 현재 가격표 기준 재계산 시 <strong>{totalPrice.toLocaleString()}원</strong>이 됩니다.
                                항목을 수정하지 않고 저장하면 당시 금액({snapshotTotal.toLocaleString()}원)이 그대로 유지됩니다.
                            </Alert>
                        }

                        {itemsChanged &&
                            <Alert variant="warning" className="text-center">
                                <FaExclamationCircle className="me-2" />
                                <strong>주의:</strong> 메뉴/옵션/조정액이 변경되었습니다. 저장 시 당시 스냅샷({snapshotTotal != null ? snapshotTotal.toLocaleString() + '원' : '-'})이 현재 가격 기준 금액({totalPrice.toLocaleString()}원)으로 <strong>덮어써집니다</strong>.
                            </Alert>
                        }

                        <hr />

                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>코멘트</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="추가 코멘트를 입력하세요."
                                        disabled={!canEditRecords}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>결제 수단</Form.Label>
                                    <Form.Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} disabled={!canEditRecords}>
                                        <option value="카드">카드</option>
                                        <option value="현금">현금</option>
                                        <option value="이체">이체</option>
                                        <option value="포인트">포인트</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => navigate('/admin/records')} className="me-2">취소</Button>
                            <Button variant="success" type="submit" disabled={isSaving || !canEditRecords}>
                                {isSaving ? <Spinner size="sm" /> : '저장 후 닫기'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};
export default EditServiceRecordPage;
